import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import FlexLink from '@components/FlexLink';
import TimeAgo from '@components/TimeAgo';
import UserProfileLink from '@components/User/UserProfileLink';
import { userName } from '@components/User/userHelpers';
import { useCurrentUser, useEventTracking } from '@hooks';
import { forum_post_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { ForumPost } from '@utils/backend-api/forumPosts';
import { isClosedPoll, isStandalonePollPost } from '@utils/backend-api/polls';
import { stripHtml } from '@utils/formatting';

import ForumPostActions from '../ForumPostActions';
import ForumTags from '../ForumTags';
import PollCard from '../PollCard';
import PollResult from '../PollResult';
import styles from './styles.module.scss';

export default function ForumPostListItem({ forumPost }: { forumPost: ForumPost }) {
  const { trackEvent } = useEventTracking();
  const { currentUser } = useCurrentUser();

  const {
    comment: { user },
  } = forumPost;

  const handleUserProfileLinkClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.ProfileAndArchive, contentId: forumPost.id }));
  };

  const handleForumLinkClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.ForumPost, contentId: forumPost.id }));
  };

  const isPollOnly = isStandalonePollPost(forumPost);
  const isPollClosed = isClosedPoll(forumPost.poll);
  const canVote = !!currentUser?.is_subscriber && !!forumPost.poll && !isPollClosed;
  const userVoted = forumPost.poll?.user_voted;
  const snippetText = stripHtml(forumPost.comment?.snippet || '').trim();

  const showBodySnippet =
    !!snippetText && (!forumPost.poll || snippetText !== forumPost.poll.question.trim());

  return (
    <div className={styles.forumPost} data-test="forum-post">
      {forumPost.poll && (
        <div className="mb-4 font-suisse-intl-mono text-overline-md text-primary-magenta">Poll</div>
      )}
      <FlexLink
        href={forum_post_path(forumPost.id)}
        className={styles.forumPostHeading}
        onClickCapture={handleForumLinkClick}
      >
        <div className={styles.forumPostTitle}>{forumPost.title}</div>
        <TimeAgo className={styles.forumPostDate} date={forumPost.created_at} timeFromNow />
      </FlexLink>
      <div className={styles.postedBy}>
        Posted by
        <UserProfileLink
          user={user}
          className={styles.userInfo}
          onClickCapture={handleUserProfileLinkClick}
        >
          {userName(user)}
          {!!user.company_name && (
            <>
              <div className={styles.userInfoSeparator} />
              <div>{user.company_name}</div>
            </>
          )}
        </UserProfileLink>
      </div>
      {showBodySnippet && (
        <FlexLink
          href={forum_post_path(forumPost.id)}
          className={styles.forumPostBody}
          onClickCapture={handleForumLinkClick}
        >
          {ReactHtmlParser(stripHtml(forumPost.comment.snippet))}
        </FlexLink>
      )}
      {forumPost.poll && (
        <div className={styles.forumPostPoll}>
          <PollCard poll={forumPost.poll} forumPostId={forumPost.id} showQuestion={!isPollOnly} />
        </div>
      )}
      <div className={styles.forumPostFooter}>
        {forumPost.poll && (
          <PollResult
            totalVotes={forumPost.poll.total_votes}
            pollClosed={isPollClosed}
            canVote={canVote}
            userVoted={userVoted}
          />
        )}
        <ForumTags tags={forumPost.entity_tags} small={true} />
        <ForumPostActions forumPost={forumPost} minimal={true} />
      </div>
    </div>
  );
}
